// Archival events — carousel shows a curated subset; detail pages use the same list

import { ABOUT_GIRMITIYA, EVENTS, mediaPath } from "@/lib/media";
import { cleanCopyDeep } from "@/lib/formatCopy";

const rawArchivalEventsData = [
    {
        slug: "g20-cultural-welcome",
        title: "Girmitiya Welcome at G-20 Summit",
        date: "December 20, 2024",
        location: "Johannesburg, South Africa",
        region: "G-20 Summit",
        category: "Cultural Welcome",
        image: EVENTS.g20.screenshot,
        imageAlt: EVENTS.g20.screenshotAlt,
        video: EVENTS.g20.video,
        summary:
            "Indian-origin Girmitiya community members welcomed Prime Minister Narendra Modi with the traditional Bhojpuri song \"He Ganga Maiya\" during the G-20 Summit visit.",
        description: [
            "During Prime Minister Narendra Modi's visit to South Africa for the G-20 Summit, members of the Indian-origin Girmitiya community organised a moving cultural welcome.",
            "The highlight was a live performance of the traditional Bhojpuri folk song \"He Ganga Maiya\" (Hey Ganga Maiya), sung with classical instruments as diaspora members gathered in traditional attire.",
            "The occasion celebrated the strength of the Girmitiya Indian diaspora and their enduring connection to Indian culture, traditions, and heritage across generations.",
            "The gathering reflected the remarkable journey of indentured labourers whose descendants today stand as successful, respected contributors to the nations where their ancestors settled.",
        ],
        gallery: [
            { type: "image", src: EVENTS.g20.screenshot, alt: EVENTS.g20.screenshotAlt },
            { type: "video", src: EVENTS.g20.video, alt: "Video of Girmitiya cultural welcome at G-20 Summit" },
        ],
    },
    {
        slug: "netherlands-rajasthani-cultural-program",
        title: "Rajasthani Folk Dance in The Hague",
        date: "May 2025",
        location: "The Hague, Netherlands",
        region: "Netherlands",
        category: "Cultural Program",
        image: EVENTS.netherlands.image02,
        imageAlt: EVENTS.netherlands.imageAlt,
        video: EVENTS.netherlands.video,
        summary:
            "A community program in The Hague showcased Rajasthani folk dance and the vibrant traditions of the Indian diaspora in the Netherlands.",
        description: [
            "In the Netherlands, Indians have maintained a deep connection with their heritage. During a community program in The Hague, audiences witnessed the unique colours of Rajasthan's culture and traditions.",
            "Traditional dancers in Rajasthani attire performed folk dance (lok nritya) as part of a cultural welcome and diaspora celebration.",
            "The event reflected Indian cultural diplomacy abroad and the Girmitiya community's commitment to preserving heritage across generations.",
        ],
        gallery: [
            { type: "image", src: EVENTS.netherlands.image01, alt: EVENTS.netherlands.imageAlt },
            { type: "image", src: EVENTS.netherlands.image02, alt: EVENTS.netherlands.imageAlt },
            { type: "image", src: EVENTS.netherlands.image03, alt: EVENTS.netherlands.imageAlt },
            { type: "video", src: EVENTS.netherlands.video, alt: "Rajasthani folk dance performance in The Hague" },
            { type: "video", src: EVENTS.netherlands.videoAlt, alt: "Additional recording of the Hague cultural program" },
        ],
    },
    {
        slug: "international-seminar-varanasi",
        title: "International Seminar on Pravasi Bharatiya & Vishwa Hindi Divas",
        date: "January 10, 2024",
        location: "Varanasi, India",
        region: "India",
        category: "International Seminar",
        image: EVENTS.seminars.varanasi01,
        imageAlt: EVENTS.seminars.varanasiAlt,
        imagePosition: "center 22%",
        summary:
            "A two-day international seminar on Pravasi Bharatiya Divas and World Hindi Day, jointly organised with Banaras Hindu University.",
        description: [
            "The Global Girmitiya Council joined Banaras Hindu University, Varanasi, for a two-day international seminar marking Pravasi Bharatiya Divas and Vishwa Hindi Divas.",
            "Dignitaries on stage released commemorative publications and addressed scholars, students, and community members in a historic university hall.",
            "The programme placed Girmitiya history, Hindi in the diaspora, and the global Indian indenture legacy at the centre of academic and public conversation.",
        ],
        gallery: [
            { type: "image", src: EVENTS.seminars.varanasi01, alt: EVENTS.seminars.varanasiAlt },
            { type: "image", src: EVENTS.seminars.varanasi02, alt: EVENTS.seminars.varanasiAlt },
            { type: "image", src: EVENTS.seminars.varanasi03, alt: EVENTS.seminars.varanasiAlt },
        ],
    },
    {
        slug: "mother-language-day-reflection",
        title: "International Mother Language Day & Girmitiya Memory",
        date: "February 21, 2025",
        location: "Global",
        region: "Global",
        category: "Awareness",
        image: ABOUT_GIRMITIYA.meaningAndHistory.src,
        imageAlt: ABOUT_GIRMITIYA.meaningAndHistory.alt,
        summary:
            "Reflecting on International Mother Language Day and the Girmitiya communities who preserved Bhojpuri, Awadhi, Tamil, and Hindi across oceans.",
        description: [
            "International Mother Language Day reminds us that linguistic diversity is not only celebration but remembrance, especially for communities who kept mother tongues alive under adversity.",
            "The Girmitiya story is one of resilience: from the 19th century, millions left India under the \"girmit\" (agreement) system for Mauritius, Suriname, Fiji, Guyana, Trinidad, and beyond.",
            "Languages such as Bhojpuri, Awadhi, Magahi, Hindi, and Tamil became foundations of identity in new homelands, evolving into distinct diaspora dialects.",
        ],
        gallery: [
            { type: "image", src: ABOUT_GIRMITIYA.meaningAndHistory.src, alt: ABOUT_GIRMITIYA.meaningAndHistory.alt },
            { type: "image", src: ABOUT_GIRMITIYA.culturalImpact.src, alt: ABOUT_GIRMITIYA.culturalImpact.alt },
        ],
    },
    {
        slug: "guyana-parliament-hindi",
        title: "Hindi in the Parliament of Guyana",
        date: "February 11, 2025",
        location: "Georgetown, Guyana",
        region: "Guyana",
        category: "Diaspora Milestone",
        image: ABOUT_GIRMITIYA.culturalImpact.src,
        imageAlt: ABOUT_GIRMITIYA.culturalImpact.alt,
        video: EVENTS.guyana.video01,
        summary:
            "The echo of Hindi in Guyana's Parliament, a Girmitiya history that has become a global cultural resonance.",
        description: [
            "Hindi's presence in the Parliament of Guyana represents more than policy. It is the sound of Girmitiya history finding institutional voice.",
            "The Global Girmitiya Council shared this milestone to show how diaspora communities transformed indenture-era migration into lasting political and cultural presence.",
            "From plantation labour to parliamentary speech, the Indo-Guyanese journey embodies the arc the Council documents and celebrates.",
        ],
        gallery: [
            { type: "image", src: ABOUT_GIRMITIYA.culturalImpact.src, alt: ABOUT_GIRMITIYA.culturalImpact.alt },
            { type: "video", src: EVENTS.guyana.video01, alt: "Hindi spoken in the Parliament of Guyana" },
            { type: "video", src: EVENTS.guyana.video02, alt: "Additional recording from Guyana Parliament session" },
        ],
    },
];

export const archivalEventsData = cleanCopyDeep(rawArchivalEventsData);

/** Events shown in the archival retrospective carousel */
export const archivalCarouselEvents = archivalEventsData;

export const getArchivalEvent = (slug) =>
    archivalEventsData.find((e) => e.slug === slug) ?? null;

export const mediaSrc = mediaPath;

export const getEventCardMedia = (event) => {
    if (event?.image) return { type: "image", src: event.image, alt: event.imageAlt || event.title };
    if (event?.video) return { type: "video", src: event.video, alt: event.title };
    return null;
};
